const NULL_BAR_COUNT = 50000;
const NULL_SEED = 42;
const HURST_LAGS = [2, 4, 8, 16, 32];

export const DEFAULT_D_REGIME_SETTINGS = Object.freeze({
  window: 202,
  vrLag: 3,
  erPeriod: 22,
  adxPeriod: 7,
  nullQuantile: 0.958,
  adxTrend: 20.7,
  adxRange: 7.7,
  enterTrend: 2.54,
  exitTrend: 0,
  enterRange: -2.94,
  exitRange: 0,
});

export const D_REGIME_SETTING_DEFS = [
  { key: "window", label: "Hurst / VR window", min: 30, max: 2000, step: 1 },
  { key: "vrLag", label: "Variance ratio lag", min: 2, max: 50, step: 1 },
  { key: "erPeriod", label: "Efficiency period", min: 2, max: 500, step: 1 },
  { key: "adxPeriod", label: "ADX period", min: 2, max: 100, step: 1 },
  { key: "nullQuantile", label: "Random-walk quantile", min: 0.51, max: 0.999, step: 0.001 },
  { key: "adxTrend", label: "ADX trend vote above", min: 0, max: 100, step: 0.1 },
  { key: "adxRange", label: "ADX range vote below", min: 0, max: 100, step: 0.1 },
  { key: "enterTrend", label: "Enter trend at score ≥", min: -4, max: 4, step: 0.01 },
  { key: "exitTrend", label: "Exit trend at score ≤", min: -4, max: 4, step: 0.01 },
  { key: "enterRange", label: "Enter range at score ≤", min: -4, max: 4, step: 0.01 },
  { key: "exitRange", label: "Exit range at score ≥", min: -4, max: 4, step: 0.01 },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function normalizeSettings(options = {}) {
  const value = (key) => {
    const n = Number(options[key] ?? DEFAULT_D_REGIME_SETTINGS[key]);
    return Number.isFinite(n) ? n : DEFAULT_D_REGIME_SETTINGS[key];
  };
  const normalized = {
    window: Math.round(clamp(value("window"), 30, 2000)),
    vrLag: Math.round(clamp(value("vrLag"), 2, 50)),
    erPeriod: Math.round(clamp(value("erPeriod"), 2, 500)),
    adxPeriod: Math.round(clamp(value("adxPeriod"), 2, 100)),
    nullQuantile: clamp(value("nullQuantile"), 0.51, 0.999),
    adxTrend: clamp(value("adxTrend"), 0, 100),
    adxRange: clamp(value("adxRange"), 0, 100),
    enterTrend: clamp(value("enterTrend"), -4, 4),
    exitTrend: clamp(value("exitTrend"), -4, 4),
    enterRange: clamp(value("enterRange"), -4, 4),
    exitRange: clamp(value("exitRange"), -4, 4),
  };
  if (normalized.enterTrend <= normalized.exitTrend) {
    if (normalized.exitTrend <= -4) normalized.enterTrend = -3.75;
    else normalized.exitTrend = normalized.enterTrend - 0.25;
  }
  if (normalized.enterRange >= normalized.exitRange) {
    if (normalized.exitRange >= 4) normalized.enterRange = 3.75;
    else normalized.enterRange = normalized.exitRange - 0.25;
  }
  if (normalized.adxRange >= normalized.adxTrend) {
    if (normalized.adxRange >= 100) normalized.adxRange = 99.9;
    else normalized.adxTrend = normalized.adxRange + 0.1;
  }
  return normalized;
}

function rollingStats(values, period) {
  const n = values.length;
  const sum = new Float64Array(n + 1);
  const square = new Float64Array(n + 1);
  const invalid = new Uint32Array(n + 1);
  for (let i = 0; i < n; i += 1) {
    const x = values[i];
    const valid = Number.isFinite(x);
    sum[i + 1] = sum[i] + (valid ? x : 0);
    square[i + 1] = square[i] + (valid ? x * x : 0);
    invalid[i + 1] = invalid[i] + (valid ? 0 : 1);
  }
  const mean = new Array(n).fill(NaN);
  const variance = new Array(n).fill(NaN);
  for (let i = period - 1; i < n; i += 1) {
    const start = i + 1 - period;
    if (invalid[i + 1] !== invalid[start]) continue;
    const total = sum[i + 1] - sum[start];
    const totalSquare = square[i + 1] - square[start];
    mean[i] = total / period;
    variance[i] = period > 1
      ? Math.max(0, (totalSquare - total * total / period) / (period - 1))
      : NaN;
  }
  return { mean, variance };
}

function rollingSums(values, period) {
  const n = values.length;
  const sum = new Float64Array(n + 1);
  const invalid = new Uint32Array(n + 1);
  for (let i = 0; i < n; i += 1) {
    const valid = Number.isFinite(values[i]);
    sum[i + 1] = sum[i] + (valid ? values[i] : 0);
    invalid[i + 1] = invalid[i] + (valid ? 0 : 1);
  }
  const result = new Array(n).fill(NaN);
  for (let i = period - 1; i < n; i += 1) {
    const start = i + 1 - period;
    if (invalid[i + 1] === invalid[start]) result[i] = sum[i + 1] - sum[start];
  }
  return result;
}

function lagDifference(values, lag) {
  return values.map((value, index) => index >= lag && Number.isFinite(value) && Number.isFinite(values[index - lag])
    ? value - values[index - lag]
    : NaN);
}

function computeReturnFeatures(logPrices, settings) {
  const n = logPrices.length;
  const hurst = new Array(n).fill(NaN);
  const oneBarReturns = lagDifference(logPrices, 1);
  const erReturns = lagDifference(logPrices, settings.erPeriod);
  const erDenominator = rollingSums(oneBarReturns.map(Math.abs), settings.erPeriod);
  const er = erReturns.map((value, i) => {
    const denominator = erDenominator[i];
    return Number.isFinite(value) && Number.isFinite(denominator) && denominator > 0
      ? Math.abs(value) / denominator * Math.sqrt(settings.erPeriod)
      : NaN;
  });

  const lagDiffs = HURST_LAGS.map((lag) => lagDifference(logPrices, lag));
  const lagStds = lagDiffs.map((diffs) => rollingStats(diffs, settings.window).variance.map((v) => Math.sqrt(v)));
  const logLagMean = HURST_LAGS.reduce((sum, lag) => sum + Math.log(lag), 0) / HURST_LAGS.length;
  const centeredLogLags = HURST_LAGS.map((lag) => Math.log(lag) - logLagMean);
  const denominator = centeredLogLags.reduce((sum, x) => sum + x * x, 0);
  for (let i = 0; i < n; i += 1) {
    let mean = 0;
    let usable = true;
    for (let j = 0; j < HURST_LAGS.length; j += 1) {
      const std = lagStds[j][i];
      if (!(Number.isFinite(std) && std > 0)) {
        usable = false;
        break;
      }
      mean += Math.log(std) / HURST_LAGS.length;
    }
    if (!usable) continue;
    let numerator = 0;
    for (let j = 0; j < HURST_LAGS.length; j += 1) {
      numerator += (Math.log(lagStds[j][i]) - mean) * centeredLogLags[j];
    }
    hurst[i] = numerator / denominator;
  }

  const qReturns = lagDifference(logPrices, settings.vrLag);
  const qVariance = rollingStats(qReturns, settings.window).variance;
  const oneVariance = rollingStats(oneBarReturns, settings.window).variance;
  const varianceRatio = qVariance.map((value, i) => {
    const denominatorVariance = settings.vrLag * oneVariance[i];
    return Number.isFinite(value) && Number.isFinite(denominatorVariance) && denominatorVariance > 0
      ? value / denominatorVariance
      : NaN;
  });
  return { hurst, varianceRatio, efficiency: er };
}

function makeNormalGenerator(seed) {
  let state = seed >>> 0;
  let spare = null;
  const uniform = () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return () => {
    if (spare !== null) {
      const value = spare;
      spare = null;
      return value;
    }
    const u = Math.max(Number.EPSILON, uniform());
    const v = uniform();
    const radius = Math.sqrt(-2 * Math.log(u));
    const angle = 2 * Math.PI * v;
    spare = radius * Math.sin(angle);
    return radius * Math.cos(angle);
  };
}

const thresholdCache = new Map();

function quantile(values, q) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return NaN;
  const at = clamp(q, 0, 1) * (sorted.length - 1);
  const lower = Math.floor(at);
  const upper = Math.ceil(at);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (at - lower);
}

function nullThresholds(settings) {
  const key = `${settings.window}|${settings.vrLag}|${settings.erPeriod}|${settings.nullQuantile}|${NULL_BAR_COUNT}`;
  if (thresholdCache.has(key)) return thresholdCache.get(key);
  const normal = makeNormalGenerator(NULL_SEED);
  const logPrices = new Array(NULL_BAR_COUNT);
  let price = 0;
  for (let i = 0; i < NULL_BAR_COUNT; i += 1) {
    price += normal();
    logPrices[i] = price;
  }
  const features = computeReturnFeatures(logPrices, settings);
  const thresholds = {
    hurstTrend: quantile(features.hurst, settings.nullQuantile),
    hurstRange: quantile(features.hurst, 1 - settings.nullQuantile),
    varianceRatioTrend: quantile(features.varianceRatio, settings.nullQuantile),
    varianceRatioRange: quantile(features.varianceRatio, 1 - settings.nullQuantile),
    efficiencyTrend: quantile(features.efficiency, settings.nullQuantile),
    efficiencyRange: quantile(features.efficiency, 1 - settings.nullQuantile),
  };
  thresholdCache.set(key, thresholds);
  return thresholds;
}

function calculateAdx(candles, period) {
  const result = new Array(candles.length).fill(NaN);
  const alpha = 1 / period;
  let atr = null;
  let positiveDm = null;
  let negativeDm = null;
  let adx = null;
  for (let i = 0; i < candles.length; i += 1) {
    const current = candles[i];
    const previous = candles[i - 1];
    if (![current?.high, current?.low, current?.close].every(Number.isFinite)) continue;
    let tr = current.high - current.low;
    let plusDm = 0;
    let minusDm = 0;
    if (previous && [previous.high, previous.low, previous.close].every(Number.isFinite)) {
      tr = Math.max(tr, Math.abs(current.high - previous.close), Math.abs(current.low - previous.close));
      const upMove = current.high - previous.high;
      const downMove = previous.low - current.low;
      plusDm = upMove > downMove && upMove > 0 ? upMove : 0;
      minusDm = downMove > upMove && downMove > 0 ? downMove : 0;
    }
    atr = atr === null ? tr : atr + alpha * (tr - atr);
    positiveDm = positiveDm === null ? plusDm : positiveDm + alpha * (plusDm - positiveDm);
    negativeDm = negativeDm === null ? minusDm : negativeDm + alpha * (minusDm - negativeDm);
    const plusDi = atr > 0 ? 100 * positiveDm / atr : 0;
    const minusDi = atr > 0 ? 100 * negativeDm / atr : 0;
    const diTotal = plusDi + minusDi;
    const dx = diTotal > 0 ? 100 * Math.abs(plusDi - minusDi) / diTotal : NaN;
    if (Number.isFinite(dx)) {
      adx = adx === null ? dx : adx + alpha * (dx - adx);
      result[i] = adx;
    }
  }
  return result;
}

function vote(value, low, high) {
  return value > high ? 1 : value < low ? -1 : 0;
}

/** Composite Hurst / variance-ratio / efficiency / ADX regime filter from the supplied script. */
export function detectDRegimes(candles, options = {}) {
  const settings = normalizeSettings(options);
  if (!Array.isArray(candles) || candles.length === 0) {
    return { status: "insufficient", reason: "Load candles to calculate the D regime filter.", bars: [] };
  }
  const logPrices = candles.map((bar) => Number.isFinite(bar?.close) && bar.close > 0 ? Math.log(bar.close) : NaN);
  const features = computeReturnFeatures(logPrices, settings);
  const adx = calculateAdx(candles, settings.adxPeriod);
  const thresholds = nullThresholds(settings);
  const bars = [];
  let state = 0;
  let trendBars = 0;
  let rangeBars = 0;
  let neutralBars = 0;

  for (let i = 0; i < candles.length; i += 1) {
    const values = [features.hurst[i], features.varianceRatio[i], features.efficiency[i], adx[i]];
    const warm = values.some((value) => !Number.isFinite(value));
    let score = null;
    if (warm) {
      state = 0;
    } else {
      score = vote(features.hurst[i], thresholds.hurstRange, thresholds.hurstTrend)
        + vote(features.varianceRatio[i], thresholds.varianceRatioRange, thresholds.varianceRatioTrend)
        + vote(features.efficiency[i], thresholds.efficiencyRange, thresholds.efficiencyTrend)
        + vote(adx[i], settings.adxRange, settings.adxTrend);
      if (state === 1) {
        if (score <= settings.exitTrend) state = score <= settings.enterRange ? -1 : 0;
      } else if (state === -1) {
        if (score >= settings.exitRange) state = score >= settings.enterTrend ? 1 : 0;
      } else if (score >= settings.enterTrend) {
        state = 1;
      } else if (score <= settings.enterRange) {
        state = -1;
      }
    }
    if (state === 1) trendBars += 1;
    else if (state === -1) rangeBars += 1;
    else neutralBars += 1;
    bars.push({
      time: Number(candles[i].time),
      kind: state === 1 ? "trend" : state === -1 ? "range" : "transition",
      phase: "composite",
      score,
    });
  }
  const readyBars = bars.filter((bar) => Number.isFinite(bar.score)).length;
  const firstReadyBar = bars.findIndex((bar) => Number.isFinite(bar.score));
  return {
    status: readyBars ? "ready" : "insufficient",
    reason: readyBars ? "" : `D regime needs at least ${settings.window + Math.max(32, settings.vrLag, settings.erPeriod)} warm-up candles.`,
    bars,
    settings,
    thresholds,
    readyBars,
    warmupBars: firstReadyBar < 0 ? candles.length : firstReadyBar,
    stateShare: {
      trend: trendBars / candles.length,
      range: rangeBars / candles.length,
      neutral: neutralBars / candles.length,
    },
  };
}
